import { BillableServiceCanadaStates, BillableServiceCountries } from "@/Administration/Models/UI/Enums/Administration";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { EnvironmentTax } from "@/Common/Helpers/EnvironmentTax";
import { BillableServiceItem } from "@/Common/Models/BillableServiceItem";
import { Address } from "@/Common/Models/Contacts/Address";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";

class BillableServiceUIModel implements ICheckable {
    private _selectedSite: SitesModel;

    public Id: string;
    public AlternativeId: string;
    public Checked: boolean;
    public Visible: boolean;
    public Target: CheckboxTarget;
    public Name: string;
    public Number: string;
    public Price: number;
    public Model: BillableServiceItem;
    public StateTaxes: BillableServiceCanadaStates;
    public labelQST: string;
    public labelGST: string;
    public labelHST: string;
    public labelGenericLabel: string;
    public ShowHST: boolean;

    constructor() {
        this.Id = "";
        this.AlternativeId = "";
        this.Name = "";
        this.Price = 0;
        this.Checked = false;
        this.Visible = true;
        this.Target = CheckboxTarget.Unassigned;
        this.SiteId = "";
        this.Model = new BillableServiceItem();
        this.StateTaxes = this.getCurrentState(cacheStore.GetDefaultRegionForActiveSite().LookupCode);
        this.labelGST = cacheStore.GetString("GstMLabel");
        this.labelQST = EnvironmentTax.getPstOrQstLabel();
        this.labelHST = cacheStore.GetString("HstMLabel");
        this.labelGenericLabel = cacheStore.GetString("TaxLabel");
        this.ShowHST = !this.SiteId && EnvironmentTax.hasHst();
    }

    public get SiteId(): string {
        return this.Model?.SiteId ?? "";
    }

    public set SiteId(value: string) {
        if (!this.Model)
            return;

        this.Model.SiteId = value ?? "";
        this._selectedSite = cacheStore.GetSiteById(value);
        this.labelQST = this._selectedSite?.getPstOrQstLabel() ?? EnvironmentTax.getPstOrQstLabel();
    }

    public clone(): BillableServiceUIModel {
        var billableServiceModelClone: BillableServiceItem = JSON.parse(JSON.stringify(this.Model));
        return BillableServiceUIModel.fromCompositeObject(billableServiceModelClone);
    }

    public static fromCompositeObject(billableServiceComposite: BillableServiceItem): BillableServiceUIModel {
        var o = new BillableServiceUIModel();

        o.Model = billableServiceComposite;
        o.labelQST = EnvironmentTax.getPstOrQstLabel();

        if (billableServiceComposite != null) {
            o.Id = billableServiceComposite.BillableServiceId;
            let name = "";
            let lang = sessionStore.userLanguageCode;
            if (lang == "en")
                name = billableServiceComposite.NameEn;
            else if (lang == "fr")
                name = billableServiceComposite.NameFr;
            else
                name = billableServiceComposite.NameEs;
            o.Name = name;
            o.Price = billableServiceComposite.Price;
            o.SiteId = billableServiceComposite.SiteId;

            o.Number = billableServiceComposite.Number;

            o.labelQST = cacheStore.GetSiteById(o.SiteId)?.getPstOrQstLabel() ?? EnvironmentTax.getPstOrQstLabel();
        }

        o.getFormatedTaxes(o);
        o.labelGST = cacheStore.GetString("GstMLabel");
        o.labelHST = cacheStore.GetString("HstMLabel");
        o.labelGenericLabel = cacheStore.GetString("TaxLabel");
        return o;
    }

    public static getName(e: BillableServiceItem, lang: string): string {
        let name = "";
        if (lang == "en")
            name = e.NameEn;
        else if (lang == "fr")
            name = e.NameFr;
        else
            name = e.NameEs;

        return name;
    }

    public getCostCentersBySiteId(): PrivateBillCostCenter[] {
        let centers = [];
        if (this.Model.SiteId != null) {
            if (cacheStore.CostCenterCache && cacheStore.CostCenterCache.length > 0)
                centers = cacheStore.CostCenterCache.filter(x => x.SiteId == this.Model.SiteId);
        }
        return centers;
    }

    public getSitesByCostCenter(): SitesModel[] {
        let sites = [];
        if (cacheStore.CostCenterCache && cacheStore.CostCenterCache.length > 0) {
            let sitesInCenterCost = cacheStore.CostCenterCache.filter(x => x.ID == this.Model.CostCenterId);
            if (sitesInCenterCost && sitesInCenterCost.length > 0) {
                sites.push(cacheStore.GetSiteById(sitesInCenterCost[0].SiteId));
            }
        }
        return sites;
    }

    public static getAllSitesExistInCostCenter(): SitesModel[] {
        let sites = [];
        let centers = cacheStore.CostCenterCache;
        let sitesCache = cacheStore.SiteCache;
        if (centers && centers.length > 0) {
            for (let center of centers) {
                let sitesInCenterCost = sitesCache.filter(x => x.Model.Id == center.SiteId);
                if (sitesInCenterCost && sitesInCenterCost.length > 0) {
                    sites.push(sitesInCenterCost[0]);
                }
            }
        }
        return sites;
    }

    public static isEnNameValid(e: BillableServiceItem): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.NameEn);
    }

    public static isFrNameValid(e: BillableServiceItem): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.NameFr);
    }

    public static isEsNameValid(e: BillableServiceItem): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.NameEs);
    }

    public static isPriceValid(e: BillableServiceItem): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.Price.toString());
    }

    public static isNumberValid(e: BillableServiceItem): boolean {
        if (e == null) return false;
        return !IsNullOrWhiteSpace(e.Number);
    }

    public getFormatedTaxes(serviceUI?: BillableServiceUIModel): BillableServiceUIModel {
        let o = serviceUI;

        if (o.SiteId && o.SiteId != "") {
            let sitesAddress = cacheStore.SitesAddressCache;
            let countries = cacheStore.GetCountryLookups();

            let site = sitesAddress.filter((x: Address) => x.SiteId == o.SiteId);

            if (site && site.length > 0) {
                let count = countries.filter(
                    (x: FlatLookupView) => x.LookupID == site[0].CountryLookupId
                );

                if (count && count.length > 0) {
                    let states = cacheStore.GetLookupById(site[0].StateLookupId);
                    o.StateTaxes = this.getCurrentState(states.LookupCode);
                }
            }
        }
        else {
            let currentState = cacheStore.GetDefaultRegionForActiveSite();
            o.StateTaxes = this.getCurrentState(currentState.LookupCode);
            //  o.CostCenterId = "";
        }
        return o;
    }

    public getCurrentCountry(lookUpCode?: string): BillableServiceCountries {
        let country = BillableServiceCountries.Canada;

        if (lookUpCode == "CNCANADA")
            country = BillableServiceCountries.Canada;
        else if (lookUpCode == "CNUSA")
            country = BillableServiceCountries.USA;
        else if (lookUpCode == "CNMEXICO")
            country = BillableServiceCountries.Mexico;
        else if (lookUpCode == "CNCOLOMBIA")
            country = BillableServiceCountries.Colombia;
        return country;
    }

    public getCurrentState(lookUpCode?: string): BillableServiceCanadaStates {
        let state = BillableServiceCanadaStates.Quebec;

        if (lookUpCode == "CanadaState_QC")
            state = BillableServiceCanadaStates.Quebec;
        else if (lookUpCode == "CanadaState_ON")
            state = BillableServiceCanadaStates.Ontario;
        else if (lookUpCode == "CanadaState_NB")
            state = BillableServiceCanadaStates.NewBrunswick;

        return state;
    }

    public getStringTaxesForTable(): string {
        if (!this.Model.HasGST && !this.Model.HasQST && !this.Model.HasHST && !this.Model.HasGenericTax)
            return "--";

        let strTaxes = [];
        if (this.Model.HasHST)
            strTaxes.push(cacheStore.GetString("HstMLabel"));

        if (this.Model.HasGST)
            strTaxes.push(cacheStore.GetString("GstMLabel"));

        if (this.Model.HasQST)
            strTaxes.push(this._selectedSite?.getPstOrQstLabel() ?? EnvironmentTax.getPstOrQstLabel());

        if (this.Model.HasGenericTax)
            strTaxes.push(cacheStore.GetString("TaxLabel"));

        return strTaxes.join("/");
    }
}

export { BillableServiceUIModel };
